/**
 * fuenteDeMinuta.js — de dónde salió un acta, y si se puede borrar.
 *
 * ── Por qué hace falta saberlo ───────────────────────────────────────────
 *
 * Porque no todas las actas se borran igual. Una que salió de una reunión de
 * Teams tiene la grabación detrás: si se borra, la tarjeta sigue en el inicio
 * —es la reunión, no el acta— y se puede volver a generar en un clic. Borrarla
 * es deshacer un trabajo.
 *
 * Una que salió de un texto pegado, de un archivo de subtítulos o de un audio
 * no tiene nada detrás. No hay cita en el calendario, no hay grabación, y el
 * archivo original se quedó en el equipo de quien lo subió. Es una fila que
 * aparece sola en la lista y que hasta hoy **no había manera de quitar**: una
 * prueba, un audio que se transcribió mal, un acta duplicada por subir dos
 * veces el mismo .vtt. Esas son las que se borran, y por eso el borrado se
 * ofrece solo ahí.
 *
 * ── Por qué va en `SourceType` y no en un sitio nuevo ────────────────────
 *
 * Porque la columna ya existía para esto —`0 = Teams, 1 = Manual`— y nadie la
 * usaba: el backend escribía `1` en todas, también en las de Teams. Añadir un
 * campo al lado habría dejado dos verdades sobre lo mismo. Y `SourceType` viene
 * en el listado del inicio, así que distinguir el origen no cuesta una consulta
 * más por tarjeta.
 *
 * ── La parte incómoda: lo que ya está guardado ───────────────────────────
 *
 * Todas las actas anteriores a este cambio tienen `SourceType = 1`, vengan de
 * Teams o no. El código no puede preguntarles. Así que la clasificación es
 * deliberadamente **conservadora**: solo se considera manual lo que lo demuestra
 * —un código nuevo e inequívoco, o la marca que `SubirTranscripcion` deja en
 * `recordingId`—, y todo lo demás se trata como de Teams.
 *
 * El error se elige a propósito en esa dirección. Clasificar de más haría
 * aparecer un botón de borrar sobre un acta de una reunión real; clasificar de
 * menos solo hace que el botón no aparezca donde podría. Lo primero destruye
 * trabajo, lo segundo se arregla abriendo el acta.
 */

/**
 * Los cuatro orígenes.
 *
 * `codigo` es lo que se guarda en `Minutes.SourceType`. El 0 y el 1 ya tenían
 * ese significado desde la v1 y se respetan; el 2 y el 3 son nuevos.
 */
export const FUENTES = {
  teams: {
    codigo: 0,
    etiqueta: 'Teams',
    icono: '🎥',
    detalle: 'Salió de una reunión grabada en Teams.'
  },
  archivo: {
    codigo: 1,
    etiqueta: 'Archivo',
    icono: '📎',
    detalle: 'Salió de un archivo de transcripción que se subió a mano.'
  },
  pegado: {
    codigo: 2,
    etiqueta: 'Texto',
    icono: '📝',
    detalle: 'Salió de un texto que se pegó a mano.'
  },
  audio: {
    codigo: 3,
    etiqueta: 'Audio',
    icono: '🎙️',
    detalle: 'Salió de una grabación de audio que se transcribió aquí.'
  }
};

/** Las tres que se crean subiendo algo, y que por tanto se pueden borrar. */
export const MANUALES = ['archivo', 'pegado', 'audio'];

/**
 * La marca que `SubirTranscripcion` deja en `recordingId`.
 *
 * No es decorativa: es lo que impide que una de estas actas se empareje con una
 * reunión del calendario por parecerse en el título (ver `emparejarMinutas.js`).
 * Aquí sirve además como prueba de origen para las creadas antes de que se
 * escribiera `SourceType`.
 */
export const MARCA_MANUAL = 'transcripcion-';

/** Lo que elige la persona en el diálogo → el código que se guarda. */
export function codigoDeVia(via) {
  if (via === 'audio') return FUENTES.audio.codigo;
  if (via === 'pegar') return FUENTES.pegado.codigo;
  if (via === 'archivo') return FUENTES.archivo.codigo;
  // Un `via` que no se reconoce no se inventa un origen: se guarda como manual
  // genérico, que es lo único que se sabe con certeza (llegó por este diálogo).
  return FUENTES.archivo.codigo;
}

/**
 * De dónde salió un acta.
 *
 * @param {Object} minuta - la del listado (`sourceType`, `recordingId`)
 * @returns {'teams'|'archivo'|'pegado'|'audio'}
 */
export function fuenteDeMinuta(minuta) {
  if (!minuta) return 'teams';

  const codigo = minuta.sourceType == null ? null : Number(minuta.sourceType);

  // Códigos nuevos: los escribe solo el código de hoy, así que no hay duda.
  if (codigo === FUENTES.audio.codigo) return 'audio';
  if (codigo === FUENTES.pegado.codigo) return 'pegado';
  if (codigo === FUENTES.teams.codigo) return 'teams';

  // `1` es ambiguo —se escribía en todas—, así que hace falta la segunda
  // prueba: la marca en `recordingId`.
  const marcada = String(minuta.recordingId || '').startsWith(MARCA_MANUAL);
  if (codigo === FUENTES.archivo.codigo && marcada) return 'archivo';

  return 'teams';
}

/** La ficha del origen, para enseñarlo. */
export function datosDeFuente(minuta) {
  return FUENTES[fuenteDeMinuta(minuta)];
}

/**
 * ¿Se puede borrar esta acta?
 *
 * Solo las que se crearon subiendo algo. Ver la cabecera: una de Teams tiene su
 * reunión detrás y se regenera; estas no tienen nada y son las que se quedan
 * atascadas en la lista.
 */
export function sePuedeEliminar(minuta) {
  return Boolean(minuta) && MANUALES.includes(fuenteDeMinuta(minuta));
}

/** Por qué no se puede, para decirlo en vez de dejar un botón gris. */
export function porQueNoSeElimina(minuta) {
  if (!minuta) return 'No hay ningún acta abierta.';
  if (sePuedeEliminar(minuta)) return null;
  return 'Esta acta salió de una reunión de Teams, así que se borra desde la '
    + 'reunión y no desde aquí. Aquí solo se borran las que se crearon subiendo '
    + 'un texto, un archivo o un audio.';
}

const fuenteDeMinutaApi = {
  FUENTES, MANUALES, MARCA_MANUAL,
  codigoDeVia, fuenteDeMinuta, datosDeFuente, sePuedeEliminar, porQueNoSeElimina
};
export default fuenteDeMinutaApi;
